/**
 * Public /invite landing page — Phase 5A Member-Get-Member.
 *
 * When a user clicks an invite link in WhatsApp / messaging app on
 * desktop or a browser without the native app installed, the link
 * resolves to /invite?brand=<slug>&ref=<code>. Without this page they
 * see an Expo-router "Unmatched Route" error.
 *
 * What this renders:
 *   - OneStamp wordmark
 *   - Brand context ("You've been invited to <Brand>")
 *   - Big referral code chip (selectable text for manual entry)
 *   - App-store buttons (placeholders; wire when builds exist)
 *   - Android intent-style deeplink button that tries to open the
 *     native app, falls through to the store on failure
 *
 * NOTE: This is a public page — no auth, no API token required.
 * Brand name lookup uses /api/brands/by-slug if available, otherwise
 * we just show the slug capitalised.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Apple, Smartphone, Copy, Check, Gift } from 'lucide-react';

function prettifySlug(slug) {
  if (!slug) return '';
  // Replace hyphens and underscores with spaces, then split letter→digit
  // and digit→letter boundaries (so "salemba43" → "Salemba 43" and
  // "kopi-abc" → "Kopi ABC").
  return slug
    .replace(/[-_]+/g, ' ')
    .replace(/([a-zA-Z])(\d)/g, '$1 $2')
    .replace(/(\d)([a-zA-Z])/g, '$1 $2')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default function InvitePage() {
  const [params] = useSearchParams();
  const refCode = (params.get('ref') || '').toUpperCase();
  const brandSlug = params.get('brand') || '';
  const [copied, setCopied] = useState(false);
  const [brandName, setBrandName] = useState('');

  const fallbackName = useMemo(() => prettifySlug(brandSlug), [brandSlug]);

  // Try a public brand lookup. We attempt /api/brands but it requires
  // auth — silently fall back to the slug-derived name if it fails.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!brandSlug) return;
      try {
        const base = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${base}/api/brands/by-slug/${brandSlug}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.name) setBrandName(data.name);
      } catch {
        // Public brand lookup not available — show the prettified slug.
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [brandSlug]);

  const displayName = brandName || fallbackName || 'a featured brand';

  const onCopy = async () => {
    if (!refCode) return;
    try {
      await navigator.clipboard.writeText(refCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // best-effort
    }
  };

  const onOpenInApp = () => {
    // Native scheme — works if onestamp:// is registered on device.
    // On desktop browsers this just fails silently which is fine.
    const url = `onestamp://invite?brand=${encodeURIComponent(
      brandSlug
    )}&ref=${encodeURIComponent(refCode)}`;
    window.location.href = url;
  };

  return (
    <div
      className="min-h-screen bg-bg-base text-white overflow-hidden relative"
      data-testid="invite-landing"
    >
      {/* Backdrop glow */}
      <div className="absolute inset-x-0 top-0 h-[60vh] bg-gradient-to-b from-gold/15 via-gold/3 to-transparent pointer-events-none" />

      <div className="relative max-w-md mx-auto px-6 pt-16 pb-12">
        {/* Wordmark */}
        <div className="flex items-center gap-2 mb-12">
          <span className="font-display text-2xl font-bold tracking-tight">
            One<span className="text-gold">Stamp</span>
          </span>
        </div>

        {/* Hero */}
        <div className="w-16 h-16 rounded-2xl bg-gold/15 border border-gold/30 grid place-items-center mb-6">
          <Gift size={28} className="text-gold" />
        </div>

        <div className="text-[11px] uppercase tracking-[0.18em] text-gold/90 font-semibold mb-2">
          You've been invited
        </div>
        <h1 className="font-display text-4xl font-bold leading-tight tracking-tight">
          Join {displayName} on OneStamp.
        </h1>
        <p className="text-white/55 text-base mt-4 leading-relaxed">
          A friend invited you to collect digital stamps from {displayName}.
          Sign up and you'll both unlock a bonus stamp on your first visit.
        </p>

        {/* Code chip */}
        {refCode ? (
          <div
            className="mt-8 bg-[#15151B] border border-gold/30 rounded-2xl p-5 flex flex-col items-center"
            data-testid="invite-code-card"
          >
            <span className="text-[10px] uppercase tracking-[0.2em] text-gold/90 font-semibold">
              Your referral code
            </span>
            <div
              className="font-display text-4xl font-bold tracking-[6px] text-white mt-2 select-all"
              data-testid="invite-code"
            >
              {refCode}
            </div>
            <button
              type="button"
              onClick={onCopy}
              className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-gold hover:text-gold-light transition-colors"
              data-testid="invite-copy-btn"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied!' : 'Tap to copy'}
            </button>
            <p className="text-[11px] text-white/40 mt-3 text-center leading-relaxed">
              Enter this code when you sign up in the OneStamp mobile app to
              claim your welcome bonus.
            </p>
          </div>
        ) : (
          <div className="mt-8 rounded-2xl border border-white/10 bg-[#15151B] p-5 text-sm text-white/55">
            This invite link is missing a code. Ask your friend to share a new
            one.
          </div>
        )}

        {/* CTAs */}
        <div className="mt-8 space-y-3">
          <button
            type="button"
            onClick={onOpenInApp}
            className="w-full h-14 rounded-2xl bg-gold hover:bg-gold-light text-bg-base font-semibold flex items-center justify-center gap-2 transition-colors"
            data-testid="invite-open-app"
          >
            <Smartphone size={18} />
            Open in OneStamp app
          </button>
          <div className="grid grid-cols-2 gap-3">
            <a
              href="#download-ios"
              onClick={(e) => e.preventDefault()}
              className="h-12 rounded-xl border border-white/10 hover:border-gold/30 hover:bg-white/5 flex items-center justify-center gap-2 text-xs font-semibold text-white/80 transition-colors"
              data-testid="invite-store-ios"
            >
              <Apple size={14} /> App Store
            </a>
            <a
              href="#download-android"
              onClick={(e) => e.preventDefault()}
              className="h-12 rounded-xl border border-white/10 hover:border-gold/30 hover:bg-white/5 flex items-center justify-center gap-2 text-xs font-semibold text-white/80 transition-colors"
              data-testid="invite-store-android"
            >
              <Smartphone size={14} /> Google Play
            </a>
          </div>
          <p className="text-[10px] text-white/35 text-center pt-2">
            Don't have the app? Install it, sign up with your email, and paste
            the code above on the welcome screen.
          </p>
        </div>

        {/* How it works */}
        <div className="mt-12 border-t border-white/8 pt-8">
          <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-semibold mb-4">
            How it works
          </div>
          <ol className="space-y-3">
            {[
              `Tap "Open in OneStamp app" above`,
              `Sign up with your email — code auto-fills`,
              `Visit ${displayName} for your first stamp + bonus`,
            ].map((line, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-gold/15 text-gold text-xs font-bold grid place-items-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </div>
                <span className="text-sm text-white/70 leading-relaxed">{line}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-12 text-center text-[10px] text-white/30">
          OneStamp · Premium loyalty for the brands you love.
        </div>
      </div>
    </div>
  );
}
